import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Search,
  Clear,
  FilterAlt,
  FolderOpen,
  Inventory,
  VolunteerActivism,
  TableView,
  ViewModule,
  Link,
  Numbers,
  Assessment,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';

import receiptData from "../data/aid_2024_table1.json";

const AidReceiptTable = () => {
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();
  const [filteredData, setFilteredData] = useState(receiptData);
  const [filters, setFilters] = useState({
    search: "",
    donor: "",
    item: "",
  });
  const [viewMode, setViewMode] = useState("cards");
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const result = receiptData.filter((item) => {
        return (
          (filters.search === "" ||
            item.Item.toLowerCase().includes(filters.search.toLowerCase()) ||
            item.Donor.toLowerCase().includes(filters.search.toLowerCase())) &&
          (filters.donor === "" || item.Donor === filters.donor) &&
          (filters.item === "" || item.Item === filters.item)
        );
      });
      setFilteredData(result);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      donor: "",
      item: "",
    });
  };

  const uniqueDonors = [...new Set(receiptData.map((item) => item.Donor))];
  const uniqueItems = [...new Set(receiptData.map((item) => item.Item))];

  const totalReceived = filteredData.reduce(
    (sum, item) => {
      const received = Number(item.Received);
      return sum + (isNaN(received) ? 0 : received);
    },
    0
  );
  const totalDistributed = filteredData.reduce(
    (sum, item) => {
      const distributed = Number(item.Distributed);
      return sum + (isNaN(distributed) ? 0 : distributed);
    },
    0
  );

  const StatsCard = ({ title, value, icon, color }) => (
    <Card className="elegant-card" sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main`, mb: 1 }}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  const ItemCard = ({ item }) => (
    <Card className="elegant-card animate-fade-in-up" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {getLocalizedValue(item, 'Item')}
          </Typography>
          <Chip
            label={getLocalizedValue(item, 'Donor')}
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              fontWeight: 500,
            }}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("Received")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {Number(item.Received || 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("Distributed")}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {Number(item.Distributed || 0).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t("Unit")}: {getLocalizedValue(item, 'Unit')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Donor")}: {getLocalizedValue(item, 'Donor')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box className="animate-fade-in-up">
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h2" 
          className="heading-primary"
          sx={{ 
            textAlign: 'center',
            mb: 2,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("GEC")}
        </Typography>
        <Typography 
          variant="h6" 
          className="text-elegant"
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 3
          }}
        >
          {t("Emergency Relief Aid Distribution and Handover Report")}
        </Typography>

        {/* Information Requests Button */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            component="a"
            href="https://drive.google.com/drive/folders/1nuVLP2AL6zsSr_UIRiGXpo2W7SWxkCv4?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<FolderOpen />}
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(12, 159, 193, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
                {t("Register of Information Requests")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem', color: 'white' }}>
                {t("Official Responses from Administration")}
              </Typography>
            </Box>
          </Button>
        </Box>
        
        {/* GEC Context */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            maxWidth: '900px', 
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.05) 0%, rgba(77, 182, 209, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              mb: 3
            }}
          >
            {t("The main governmental body in charge of leading emergency response and coordinating relief efforts among ministries and public agencies.")}
          </Typography>
          
          {/* Statistics Reference Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/stats/gec"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(12, 159, 193, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={<Assessment />}
            >
              {t("View Detailed Statistics")}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Items")}
            value={filteredData.length}
            icon={<Inventory />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Received")}
            value={totalReceived}
            icon={<VolunteerActivism />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Total Distributed")}
            value={totalDistributed}
            icon={<Assessment />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <StatsCard
            title={t("Unique Donors")}
            value={uniqueDonors.length}
            icon={<VolunteerActivism />}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FilterAlt sx={{ verticalAlign: 'middle', mr: 1 }} />
            {t("Filter Aid Items")}
          </Typography>
          <Box>
            <Button
              onClick={() => setViewMode(viewMode === "cards" ? "table" : "cards")}
              variant="outlined"
              sx={{ mr: 2 }}
              startIcon={viewMode === "cards" ? <TableView /> : <ViewModule />}
            >
              {viewMode === "cards" ? t("Table View") : t("Card View")}
            </Button>
            <Button
              onClick={handleReset}
              startIcon={<Clear />}
              variant="outlined"
            >
              {t("Clear Filters")}
            </Button>
          </Box>
        </Box>

        {/* Full-width search field */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={t("Search Items or Donors")}
            variant="outlined"
            value={filters.search}
            onChange={(e) => handleChange("search", e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
              sx: {
                height: "56px",
                "& input": {
                  padding: "16.5px 14px",
                },
              },
            }}
          />
        </Box>

        {/* Select fields in a row */}
        <Box sx={{ width: "100%" }}>
          <Grid
            container
            spacing={2}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr",
              },
            }}
          >
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Donor")}</InputLabel>
                <Select
                  value={filters.donor}
                  label={t("Donor")}
                  onChange={(e) => handleChange("donor", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Donors")}</MenuItem>
                  {uniqueDonors.map((donor) => (
                    <MenuItem key={donor} value={donor}>
                      {donor}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel>{t("Item")}</InputLabel>
                <Select
                  value={filters.item}
                  label={t("Item")}
                  onChange={(e) => handleChange("item", e.target.value)}
                  sx={{ height: "56px" }}
                >
                  <MenuItem value="">{t("All Items")}</MenuItem>
                  {uniqueItems.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : filteredData.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="text.secondary">
            {t("No items match your filters")}
          </Typography>
          <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReset}>
            {t("Clear Filters")}
          </Button>
        </Paper>
      ) : viewMode === "cards" ? (
        <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
          {filteredData.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <ItemCard item={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Item")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Donor")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Received")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">{t("Distributed")}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t("Unit")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                    "&:hover": { backgroundColor: "action.selected" },
                  }}
                >
                  <TableCell>{getLocalizedValue(row, 'Item')}</TableCell>
                  <TableCell>{getLocalizedValue(row, 'Donor')}</TableCell>
                  <TableCell align="right">{Number(row.Received || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(row.Distributed || 0).toLocaleString()}</TableCell>
                  <TableCell>{getLocalizedValue(row, 'Unit')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AidReceiptTable;
